"use client";

import { useEffect, useState, useEffectEvent } from "react";
import {
  getPublisherCCUChart,
  type PublisherCCUChartPoint,
} from "../api/getPublisherCCUChart";
import { isAbortError } from "@/lib/utils/error";

type UsePublisherCCUHistoryOptions = {
  clanid: number;
  from?: string | null; // YYYY-MM-DD
  to?: string | null; // YYYY-MM-DD
  bust?: number;
  enabled?: boolean;
};

export function usePublisherCCUHistory({
  clanid,
  from,
  to,
  bust,
  enabled = true,
}: UsePublisherCCUHistoryOptions) {
  const [history, setHistory] = useState<PublisherCCUChartPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStart = useEffectEvent(() => {
    setIsLoading(true);
    setError(null);
  });

  const handleSuccess = useEffectEvent((result: PublisherCCUChartPoint[]) => {
    setHistory(result);
    setIsLoading(false);
  });

  const handleError = useEffectEvent((message: string) => {
    setHistory([]);
    setError(message);
    setIsLoading(false);
  });

  useEffect(() => {
    if (!enabled) {
      handleSuccess([]);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    handleStart();

    getPublisherCCUChart({
      clanid,
      from,
      to,
      bust,
      init: { signal: controller.signal },
    })
      .then((result) => {
        if (!cancelled) {
          handleSuccess(result);
        }
      })
      .catch((err: Error) => {
        if (isAbortError(err)) return;
        if (!cancelled) {
          handleError(err.message ?? "Unknown error");
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [clanid, from, to, bust, enabled]);

  return { history, isLoading, error };
}
